import { type ItemDetails, LegacySegmentTypes } from '@1debit/analytics';
import { Cancel, ChevronLeft, MoreHorizontalFilled } from '@1debit/icons';
import { type ButtonHTMLAttributes, type CSSProperties, forwardRef } from 'react';

import { type AnalyticsId } from '../../client/AnalyticsProvider/AnalyticsProvider';
import { type AsChildProps, ClickableAnalyticsSlot } from '../../client/ClickableAnalyticsSlot';
import { classNames } from '../utils/classNames';

export type NavigationButtonVariant = 'close' | 'back' | 'menu';

type NavigationButtonDefaultProps = {
	analyticsId: AnalyticsId;
	variant: NavigationButtonVariant;
	style?: CSSProperties;
	className?: string;
	itemDetails?: ItemDetails;
	itemValue?: string;
};

export type NavigationButtonElementProps = ButtonHTMLAttributes<HTMLButtonElement> & {
	children?: never;
};

export type NavigationButtonProps = AsChildProps<NavigationButtonElementProps> & NavigationButtonDefaultProps;

const getIconAndAriaLabelByVariant = (variant: NavigationButtonVariant) => {
	const iconClasses = 'text-content-primary';
	switch (variant) {
		case 'close':
			return {
				icon: <Cancel aria-hidden className={iconClasses} data-testid="navigation-button-close-icon" />,
				ariaLabel: 'Close',
			};
		case 'menu':
			return {
				icon: <MoreHorizontalFilled aria-hidden className={iconClasses} data-testid="navigation-button-menu-icon" />,
				ariaLabel: 'See details',
			};
		case 'back':
		default:
			return {
				icon: <ChevronLeft aria-hidden className={iconClasses} data-testid="navigation-button-back-icon" />,
				ariaLabel: 'Go back',
			};
	}
};

/**
 * @deprecated Use IconButton component instead which is more general and supports any icon.
 */
export const NavigationButton = forwardRef<HTMLButtonElement, NavigationButtonProps>(
	({ variant, asChild, className, analyticsId, children, itemDetails, itemValue, ...rest }, ref) => {
		const { icon, ariaLabel } = getIconAndAriaLabelByVariant(variant);

		return (
			<div
				className={classNames(
					'p-2 w-fit sm:p-3 transition-all rounded-full hover:bg-bg-raised-secondary cursor-pointer relative active:scale-90',
					className,
				)}>
				<ClickableAnalyticsSlot
					ref={ref}
					analyticsId={analyticsId}
					trackElementType={LegacySegmentTypes.ElementType.ELEMENT_TYPE_BUTTON}
					asChild={asChild}
					aria-label={ariaLabel}
					defaultElement="button"
					itemDetails={itemDetails}
					itemValue={itemValue}
					className="absolute top-0 left-0 h-full w-full"
					{...rest}>
					{children}
				</ClickableAnalyticsSlot>
				{icon}
			</div>
		);
	},
);
